import { Recurrence } from '@tableau/self-service-schedules';
import { ButtonType, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, DialogWidget, HostedStackedElementContainer, RadioButtonWithLabelGroupWidget, TabsWidget } from '@tableau/widgets';
import React from 'react';
import { BridgeExtractScheduleDialogBody, BridgeExtractScheduleDialogProps } from '~src/app/content/BridgeExtractScheduleDialog';
import { localizeRefreshTaskType } from '~src/app/content/RefreshSelfServiceScheduleDialog';
import { Messages } from '~src/codegen/Localize';
import { ModalInstance, ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { cssClass } from '~src/style';
import { RefreshTaskType } from '~src/ts/ServerApi/requests/CreateSelfServiceScheduleExtractTasksRequest';
import { ISchedule } from '../types/ISchedule';
import { CreateExtractTaskRunNowTab } from './CreateExtractRunNowBody';
import { CreateExtractTaskRecommendedTab, PropsForCreateExtractRecommendedTab } from './CreateExtractTaskScheduleBody';
import { SchedulePickerProps } from './SchedulePickerActionBody';

export type PropsForCreateExtractTaskActionBody = Omit<SchedulePickerProps, 'scheduledActionType' | 'getSchedules'> & Omit<BridgeExtractScheduleDialogProps, 'close' | 'scheduleExtract'> & {
  renderRunNowTab: boolean;
  renderRecommendedTab: boolean;
  renderBridgeTab: boolean;
  runNowTabPrompt: string;
  close: (result?: IResult) => void;
  runNow: (type: RefreshTaskType) => Promise<IResult>;
  schedule: (type: RefreshTaskType, schedule: ISchedule) => Promise<IResult>;
  scheduleBridgeExtract: (recurrence: Recurrence, remoteAgentName: string, refreshType: string) => Promise<IResult>;
  disableIncremental: boolean;
}

export function CreateExtractTaskActionBody(props: PropsForCreateExtractTaskActionBody) {

  // Set up Run Now Tab
  const runNowCloseFunction = (type?: RefreshTaskType) => {
    if (type) {
      props.runNow(type).then(result => props.close(result));
    } else {
      props.close();
    }
  };
  const runNowTab = CreateExtractTaskRunNowTab({
    close: runNowCloseFunction,
    disableIncremental: props.disableIncremental,
    prompt: props.runNowTabPrompt,
  });

  // Set up Recommended Tab
  const scheduleCloseFunction = (type: RefreshTaskType, schedule: ISchedule) => {
    props.schedule(type, schedule).then(result => props.close(result));
  };
  const recommendedTabProps: PropsForCreateExtractRecommendedTab = {
    ...props,
    schedule: scheduleCloseFunction, // Avoid tab needing to know about close
  };
  const recommendedTab = CreateExtractTaskRecommendedTab(recommendedTabProps);

  // Set up Bridge Tab
  const scheduleBridgeExtractCloseFunction = (recurrence: Recurrence, remoteAgentName: string, refreshType: string) => {
    props.scheduleBridgeExtract(recurrence, remoteAgentName, refreshType).then(result => props.close(result));
  };
  const bridgeTabProps: BridgeExtractScheduleDialogProps = {
    ...props,
    scheduleExtract: scheduleBridgeExtractCloseFunction,
  };
  const bridgeTab = BridgeExtractScheduleDialogBody(bridgeTabProps);

  let tabs = [];
  props.renderRunNowTab && tabs.push({
    tabContent: { label: Messages.extractTasksRefreshNowMode() },
    panelContent: runNowTab,
  });
  props.renderRecommendedTab && tabs.push({
    tabContent: { label: Messages.extractTasksScheduleModeOnServer() },
    panelContent: recommendedTab,
  });
  props.renderBridgeTab && tabs.push({
    tabContent: { label: Messages.extractTasksScheduleModeOnClient() },
    panelContent: bridgeTab,
  });
  const recommendedTabIndex = props.renderRunNowTab
    ? 1
    : 0;
  const initalTab = props.renderRecommendedTab
    ? recommendedTabIndex
    : tabs.length - 1; // Else render the last tab in the list since Bridge is larger than Run Now
  return <TabsWidget
    anchorTabIndex={initalTab}
    ariaLabel={Messages.createExtracts_action()}
    initialActiveIndex={initalTab}
    tabs={tabs}
    testId='create-extract-task-tabs'
  />;
}

export function CreateExtractTaskActionDialog(props: PropsForCreateExtractTaskActionBody) {
  const body = CreateExtractTaskActionBody(props);
  return <DialogWidget
    testId="create-extract-task"
    type={DialogType.NormalWithTitleBar}
    title={Messages.createExtracts_action()}
    onCloseRequest={props.close}
    showCloseButton={true}
    positioningBehavior={DialogPositioningBehavior.Centered}
    isModal={true}
    handleGlassClick={props.close}
    isGlassTransparent={false}
    dialogWidth={500}
    dialogBodyStyle={{
      padding: 0,
      height: '100%',
    }}
  >
    {body}
  </DialogWidget>;
}

export function openCreateExtractTaskActionDialog(props: Omit<PropsForCreateExtractTaskActionBody, 'close'>) {
  return ReactModalService.openCustom<IResult>(closeAndDismiss => <CreateExtractTaskActionDialog {...{ ...props, close: closeAndDismiss.close }} />);
}
